import React, { useEffect, useRef, useState } from "react";

import client from "@culturehq/client";
import { classnames, SelectField } from "@culturehq/components";

import { t, Trans } from "app/utils";
import getEntityParams from "app/utils/getEntityParams";

import UsersModal from "components/UsersModal";

import ErrorMessage from "components/common/ErrorMessage";
import BusinessAccountsFields from "./BusinessAccountsFields";
import PersonalAccountsFields from "./PersonalAccountsFields";
import OthersAccountsFields from "./OthersAccountsFields";

const socialPostTypes = [
  { value: "business", label: t("I will share it to the business accounts") },
  { value: "personal", label: t("I will share it on my personal accounts") },
  { value: "others", label: t("I will let other users to share it in their personal accounts") }
];

const FormData = ({
  error,
  isTiktokShareable,
  profile,
  initialShareMessage,
  selectedOptions,
  setSelectedOptions,
  showOverlay,
  socialPostType,
  onChooseSocialPostChanged,
  companyAccounts,
  selectedAccounts,
  onSelectSocialMedia,
  socialMediaSelected,
  uniqueSocialMedia,
  onSocialMediaMessageChanged,
  personalAccounts,
  schedulePostType,
  onChooseSchedulePostChanged,
  story,
  timezoneOffset,
  scheduleAt,
  handleScheduleAtChange,
  onChangeActiveText,
  handleOffsetChange
}) => {
  const [usersPagination, setPagination] = useState(undefined);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeSocialMediaText, setActiveSocialMediaText] = useState("none");
  const [shareMessage, setShareMessage] = useState("");

  useEffect(
    () => {
      if (!uniqueSocialMedia.length) {
        setActiveSocialMediaText("none");
      } else {
        const linkedSocialCount = uniqueSocialMedia.filter(social => social.category === "LinkedIn").length;
        const socialMediaText = linkedSocialCount === uniqueSocialMedia.length ? "LinkedIn" : "none";
        setActiveSocialMediaText(socialMediaText);
      }
    },
    [uniqueSocialMedia]
  );

  useEffect(
    () => {
      const media = socialMediaSelected.find(media => media.category === activeSocialMediaText);
      if (media?.text?.length === 0) {
        setShareMessage(media.text);
      } else if (activeSocialMediaText === "TikTok") {
        setShareMessage("");
      } else {
        setShareMessage(media?.text || initialShareMessage);
      }
    },
    [activeSocialMediaText, initialShareMessage, socialMediaSelected]
  );

  const handleShareWithChanged = async (newOptions, _) => {
    setSelectedOptions(newOptions);
    setLoading(true);
    const { pagination, users: newUsers } = await client.listEntityUsers(
      { ...getEntityParams(newOptions) }
    );
    setUsers(newUsers);
    setPagination(pagination.totalCount === 0 ? undefined : pagination);
    setLoading(false);
  };

  const fetchUsers = async page => {
    setLoading(true);
    const { pagination, users: newUsers } = await client.listEntityUsers(
      { ...getEntityParams(selectedOptions), page }
    );
    setUsers(newUsers);
    setPagination(pagination);
    setLoading(false);
  };

  const handleChangeActiveText = value => {
    setActiveSocialMediaText(value);
    onChangeActiveText(value);
  };

  const handleShareMessageChanged = value => {
    onSocialMediaMessageChanged(value, activeSocialMediaText);
    setShareMessage(value);
  };
  const editorRef = useRef(null);

  return (
    <div className={classnames("Form-Data", { Blurred: showOverlay })}>
      {error && <ErrorMessage error={error} />}
      <SelectField
        ariaLabel={t("Choose what type of social post")}
        options={socialPostTypes}
        onChange={onChooseSocialPostChanged}
        required
        value={socialPostType}
      >
        <h4 className="FieldLabel"><Trans>Choose what type of social post</Trans></h4>
      </SelectField>
      {socialPostType === "business" && (
        <BusinessAccountsFields
          companyAccounts={companyAccounts}
          selectedAccounts={selectedAccounts}
          onSelectSocialMedia={onSelectSocialMedia}
          uniqueSocialMedia={uniqueSocialMedia}
          activeSocialMediaText={activeSocialMediaText}
          isTiktokShareable={isTiktokShareable}
          onChangeActiveText={handleChangeActiveText}
          shareMessage={shareMessage}
          onShareMessageChanged={handleShareMessageChanged}
          onShareWithChanged={handleShareWithChanged}
          selectedOptions={selectedOptions}
          schedulePostType={schedulePostType}
          onChooseSchedulePostChanged={onChooseSchedulePostChanged}
          timezoneOffset={timezoneOffset}
          scheduleAt={scheduleAt}
          handleScheduleAtChange={handleScheduleAtChange}
          handleOffsetChange={handleOffsetChange}
          editorRef={editorRef}
        />
      )}
      {socialPostType === "personal" && (
        <PersonalAccountsFields
          isTiktokShareable={isTiktokShareable}
          personalAccounts={personalAccounts}
          selectedAccounts={selectedAccounts}
          onSelectSocialMedia={onSelectSocialMedia}
          uniqueSocialMedia={uniqueSocialMedia}
          activeSocialMediaText={activeSocialMediaText}
          onChangeActiveText={handleChangeActiveText}
          shareMessage={shareMessage}
          onShareMessageChanged={handleShareMessageChanged}
          onShareWithChanged={handleShareWithChanged}
          selectedOptions={selectedOptions}
          schedulePostType={schedulePostType}
          onChooseSchedulePostChanged={onChooseSchedulePostChanged}
          timezoneOffset={timezoneOffset}
          scheduleAt={scheduleAt}
          handleScheduleAtChange={handleScheduleAtChange}
          handleOffsetChange={handleOffsetChange}
          editorRef={editorRef}
        />
      )}
      {socialPostType === "others" && (
        <OthersAccountsFields
          isTiktokShareable={isTiktokShareable}
          profile={profile}
          personalAccounts={personalAccounts}
          selectedAccounts={selectedAccounts}
          onSelectSocialMedia={onSelectSocialMedia}
          uniqueSocialMedia={uniqueSocialMedia}
          activeSocialMediaText={activeSocialMediaText}
          onChangeActiveText={handleChangeActiveText}
          shareMessage={shareMessage}
          onShareMessageChanged={handleShareMessageChanged}
          onShareWithChanged={handleShareWithChanged}
          selectedOptions={selectedOptions}
          schedulePostType={schedulePostType}
          onChooseSchedulePostChanged={onChooseSchedulePostChanged}
          timezoneOffset={timezoneOffset}
          scheduleAt={scheduleAt}
          handleScheduleAtChange={handleScheduleAtChange}
          handleOffsetChange={handleOffsetChange}
          story={story}
          editorRef={editorRef}
        />
      )}
      <UsersModal
        usersPagination={usersPagination}
        loading={loading}
        selectedOptions={selectedOptions}
        users={users}
        fetchUsers={fetchUsers}
      />
    </div>
  );
};

export default FormData;
